import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import ImageUploadBox from "../../components/ImageUploadBox";

function AddBrand() {
  const baseURL = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("profileToken");

  const initialState = {
    name: "",
    about_brand: "",
    inner_title: "",
    inner_content: "",
  };

  const [formData, setFormData] = useState(initialState);

  // ✅ Images (string = already uploaded filename, File = new upload)
  const [logoImage, setLogoImage] = useState(null);
  const [sectionBgImage, setSectionBgImage] = useState(null);
  const [headingBgImage, setHeadingBgImage] = useState(null);
  const [galleryBgImage, setGalleryBgImage] = useState(null);

  const [loading, setLoading] = useState(false);

  // ✅ Category states
  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState("");
  const [catLoading, setCatLoading] = useState(false);

  // ✅ Scroll to section if #categories is in URL
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) element.scrollIntoView({ behavior: "smooth" });
    }
  }, [location]);

  // ✅ Fetch categories for current brand
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/category/brand/${id}`, {
          headers: { "x-access-token": token },
        });
        if (data?.status && Array.isArray(data?.result)) {
          setCategories(data.result);
        } else {
          setCategories([]);
        }
      } catch (err) {
        console.error("❌ Error fetching categories:", err);
      }
    })();
  }, [id, baseURL, token]);

  // ✅ Fetch existing brand when editing
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/brand/${id}`, {
          headers: { "x-access-token": token },
        });

        if (data?.status && data?.result) {
          const b = data.result;
          setFormData({
            name: b.name || "",
            about_brand: b.about_brand || "",
            inner_title: b.inner_title || "",
            inner_content: b.inner_content || "",
          });

          setLogoImage(b.logo || null);
          setSectionBgImage(b.section_bg || null);
          setHeadingBgImage(b.heading_bg || null);
          setGalleryBgImage(b.gallery_bg || null);
        }
      } catch (err) {
        console.error("❌ Error fetching brand:", err);
        Swal.fire("Error", "Failed to fetch brand details", "error");
      }
    })();
  }, [id, baseURL, token]);

  // ✅ Handle text inputs
  const handleChange = (e) => {
    const { id: fieldId, value } = e.target;
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  // ✅ File handlers
  const handleLogoChange = (e) => setLogoImage(e.target.files?.[0] || null);
  const handleSectionBgChange = (e) => setSectionBgImage(e.target.files?.[0] || null);
  const handleHeadingBgChange = (e) => setHeadingBgImage(e.target.files?.[0] || null);
  const handleGalleryBgChange = (e) => setGalleryBgImage(e.target.files?.[0] || null);

  // ✅ helper for preview
  const getPreviewSrc = (img) => {
    if (!img) return "";
    return typeof img === "string" ? `${baseURL}/uploads/${img}` : URL.createObjectURL(img);
  };

  // ✅ Reset form
  const handleReset = () => {
    setFormData(initialState);
    setLogoImage(null);
    setSectionBgImage(null);
    setHeadingBgImage(null);
    setGalleryBgImage(null);

    setCategories([]);
    setNewCategory("");
    setEditingCategoryId(null);
    setEditingCategoryName("");
  };

  // ✅ Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      data.append("name", formData.name);
      data.append("about_brand", formData.about_brand);
      data.append("inner_title", formData.inner_title);
      data.append("inner_content", formData.inner_content);

      // ✅ append files only if new File selected
      if (logoImage && typeof logoImage !== "string") data.append("logo", logoImage);
      if (sectionBgImage && typeof sectionBgImage !== "string")
        data.append("section_bg", sectionBgImage);
      if (headingBgImage && typeof headingBgImage !== "string")
        data.append("heading_bg", headingBgImage);
      if (galleryBgImage && typeof galleryBgImage !== "string")
        data.append("gallery_bg", galleryBgImage);

      const headers = {
        "Content-Type": "multipart/form-data",
        "x-access-token": token,
      };

      if (id) {
        await axios.put(`${baseURL}/brand/update/${id}`, data, { headers });
        Swal.fire("Success", "✅ Brand updated successfully!", "success");
      } else {
        await axios.post(`${baseURL}/brand/add`, data, { headers });
        Swal.fire("Success", "✅ Brand added successfully!", "success");
      }

      navigate("/brands-list");
    } catch (err) {
      console.error("❌ Error saving brand:", err);
      Swal.fire("Error", "❌ Failed to save brand", "error");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Add new category
  const handleAddCategory = async () => {
    if (!newCategory.trim()) return Swal.fire("Error", "Enter category name", "error");

    try {
      setCatLoading(true);
      const body = { cat_name: newCategory.trim(), brand_id: id };
      const { data } = await axios.post(`${baseURL}/category/add`, body, {
        headers: { "x-access-token": token },
      });

      if (data?.status) {
        setCategories((prev) => [...prev, data.result]);
        setNewCategory("");
        Swal.fire("Success", "Category added!", "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to add category", "error");
    } finally {
      setCatLoading(false);
    }
  };

  // ✅ Start edit category
  const startEditCategory = (cat) => {
    setEditingCategoryId(cat.id);
    setEditingCategoryName(cat.cat_name || "");
  };

  // ✅ Update category
  const handleUpdateCategory = async () => {
    if (!editingCategoryId) return;
    if (!editingCategoryName.trim())
      return Swal.fire("Error", "Category name can't be empty", "error");

    try {
      const { data } = await axios.put(
        `${baseURL}/category/update/${editingCategoryId}`,
        { cat_name: editingCategoryName.trim() },
        { headers: { "x-access-token": token } }
      );

      if (data?.status) {
        setCategories((prev) =>
          prev.map((c) =>
            c.id === editingCategoryId ? { ...c, cat_name: editingCategoryName.trim() } : c
          )
        );
        setEditingCategoryId(null);
        setEditingCategoryName("");
        Swal.fire("Updated!", "Category updated successfully", "success");
      }
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to update category", "error");
    }
  };

  // ✅ Cancel edit category
  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName("");
  };

  // ✅ Delete category
  const handleDeleteCategory = async (catId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This category will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${baseURL}/category/delete/${catId}`, {
        headers: { "x-access-token": token },
      });
      Swal.fire("Deleted!", "Category has been deleted.", "success");
      setCategories((prev) => prev.filter((c) => c.id !== catId));
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to delete category", "error");
    }
  };

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-center mb-4 text-success">{id ? "Update Brand" : "Add New Brand"}</h3>

      <form onSubmit={handleSubmit}>
        <Row className="g-4">
          {/* LEFT COLUMN */}
          <Col xs={12} md={3}>
            {/* 🔹 Brand Logo Upload */}
            <div className="border border-success rounded-4 p-3 mb-4">
              <ImageUploadBox
                id="logoInput"
                label="Logo"
                value={logoImage}
                onChange={handleLogoChange}
                baseURL={baseURL}
                required={!id}
                isEdit={!!id}
                variant="dashed"
                height={220}
                fit="contain"
              />


              <div className="mt-3">
                <ImageUploadBox
                  id="galleryBgInput"
                  label="Gallery background"
                  value={galleryBgImage}
                  onChange={handleGalleryBgChange}
                  baseURL={baseURL}
                  required={!id}
                  isEdit={!!id}
                  variant="dashed"
                  height={220}
                  fit="cover"
                />
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-dark"
                className="rounded-pill px-4"
                onClick={handleReset}
                type="button"
              >
                Reset
              </Button>

              <Button
                variant="success"
                className="rounded-pill px-4"
                type="submit"
                disabled={loading}
              >
                {loading ? <Spinner size="sm" /> : id ? "Update" : "Save"}
              </Button>
            </div>
          </Col>

          {/* RIGHT COLUMN */}
          <Col xs={12} md={9}>
            {/* 🔹 Section Background Upload */}
            <div className="border border-success rounded-4 p-4 mb-4">
              <Row className="g-4">
                <Col xs={12} md={6}>
                  <ImageUploadBox
                    id="sectionBgInput"
                    label="Brand background"
                    value={sectionBgImage}
                    onChange={handleSectionBgChange}
                    baseURL={baseURL}
                    required={!id}
                    isEdit={!!id}
                    variant="dashed"
                    height={160}
                    fit="cover"
                  />
                </Col>

                {/* 🔹 Heading Background Upload */}
                <Col xs={12} md={6}>
                  <ImageUploadBox
                    id="headingBgInput"
                    label="Heading background"
                    value={headingBgImage}
                    onChange={handleHeadingBgChange}
                    baseURL={baseURL}
                    required={!id}
                    isEdit={!!id}
                    variant="dashed"
                    height={160}
                    fit="cover"
                  />

                </Col>
              </Row>
            </div>

            {/* Brand Info */}
            <div className="border border-success rounded-4 p-4">
              <h5 className="mb-3">Brand Information</h5>

              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="name" className="form-label">
                      Brand Name <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      className="form-control"
                      placeholder="Enter Brand Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="inner_title" className="form-label">
                      Inner Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      id="inner_title"
                      className="form-control"
                      placeholder="Enter Inner Title"
                      value={formData.inner_title}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="about_brand" className="form-label">
                      About Brand <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="about_brand"
                      className="form-control"
                      placeholder="Write about the brand"
                      rows={4}
                      value={formData.about_brand}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>

                <Col xs={12} md={6}>
                  <div className="mb-3">
                    <label htmlFor="inner_content" className="form-label">
                      Inner Content <span className="text-danger">*</span>
                    </label>
                    <textarea
                      id="inner_content"
                      className="form-control"
                      placeholder="Write inner content"
                      rows={4}
                      value={formData.inner_content}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </form>

      {/* ✅ Category Section (only show when updating brand) */}
      {id && (
        <section className="mt-5" id="categories">
          <h3 className="fw-bold text-center mb-4 text-success">Manage Categories for {formData.name}</h3>

          <div className="d-flex justify-content-center mb-3">
            <input
              type="text"
              placeholder="Enter new category name"
              className="form-control w-50 me-2"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            />
            <Button variant="success" onClick={handleAddCategory} disabled={catLoading}>
              {catLoading ? <Spinner size="sm" /> : "Add"}
            </Button>
          </div>

          <table className="table table-striped table-bordered align-middle text-center">
            <thead className="table-success">
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th style={{ width: 240 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr>
                  <td colSpan="3">No categories yet</td>
                </tr>
              ) : (
                categories.map((cat) => (
                  <tr key={cat.id}>
                    <td>{cat.id}</td>

                    <td>
                      {editingCategoryId === cat.id ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editingCategoryName}
                          onChange={(e) => setEditingCategoryName(e.target.value)}
                        />
                      ) : (
                        cat.cat_name
                      )}
                    </td>

                    <td>
                      {editingCategoryId === cat.id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-sm btn-success me-2"
                            onClick={handleUpdateCategory}
                          >
                            <i className="fa fa-check me-1"></i> Save
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary me-2"
                            onClick={cancelEditCategory}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary me-2"
                          onClick={() => startEditCategory(cat)}
                        >
                          <i className="fa fa-edit"></i> Edit
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDeleteCategory(cat.id)}
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

export default AddBrand;
